import { useState } from 'react';
import { useFragment } from '@apollo/client/react';
import { Trans, useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import FormHelperText from '@mui/material/FormHelperText';
import { Form, Formik, Field } from 'formik';
import Typography from '@mui/material/Typography';

import {
    CertificateTypeListItemFragmentDoc,
    ICertificateTypeListItemFragment,
    ICertificateWithScoringFragment,
    ICreateCertificateModuleInput,
} from 'graphql/types';
import { Button } from 'common/components/Button';
import { TextField } from 'common/components/FormField';
import { ExitPageAlert } from 'common/components/ExitPageAlert';
import { UnsavedChangesAlert } from 'common/components/Alerts';
import { ContentExpander } from 'common/components/ContentExpander';
import { IModuleFormValues } from 'training/types';
import { ModuleImageFormSection } from 'training/components/ModuleImageFormSection';
import { FormDeleteButton } from 'common/components/Button/FormDeleteButton';
import { CertificateFormItem } from 'training/components/CertificateFormItem';
import { FormSection } from 'common/components/FormSection';
import { Link } from 'common/components/Link';

export interface ICertificateModuleFormValues
    extends
        IModuleFormValues,
        Omit<ICreateCertificateModuleInput, 'imageId' | 'offset'> {
    certificate?: ICertificateWithScoringFragment;
}

export interface IProps {
    certificate?: ICertificateTypeListItemFragment;
    certificateId?: string;
    initialValues?: Partial<ICertificateModuleFormValues>;
    disabled?: boolean;
    edit?: boolean;
    onClose?(): void;
    onSubmit(values: ICertificateModuleFormValues): void;
    onDelete?(): void;
    onReplaceCertificate?(): void;
    moduleGroupId?: string;
}

export const CertificateModuleForm = ({
    certificate,
    certificateId,
    initialValues,
    disabled,
    edit,
    moduleGroupId,
    onClose,
    onDelete,
    onSubmit,
    onReplaceCertificate,
    ...other
}: IProps) => {
    const [translate] = useTranslation();
    const [settingsExpanded, setSettingsExpanded] = useState(false);

    // The selected certificate's normalized cache entry is populated by the
    // CertificateModuleSelectDrawer's query, so reading it back via
    // useFragment avoids an extra network request when the user picks a
    // replacement.
    const { data: certificateFragmentData } = useFragment({
        fragment: CertificateTypeListItemFragmentDoc,
        from: certificateId ? `Certificate:${certificateId}` : '',
        fragmentName: 'CertificateTypeListItem',
    });

    const selectedCertificate =
        certificateId && !!Object.keys(certificateFragmentData).length
            ? (certificateFragmentData as ICertificateTypeListItemFragment)
            : certificate;

    const initValues: ICertificateModuleFormValues = {
        title: '',
        description: '',
        titleOverride: '',
        certificateId: '',
        ...initialValues,
    };

    const selectedCertificateId = certificateId || initialValues?.certificateId;
    const certificateChanged =
        selectedCertificateId !== initialValues?.certificateId;

    const formTitle = edit
        ? translate('moduleForm.certificate.editTitle')
        : translate('moduleForm.certificate.newTitle');
    const formText = edit
        ? translate('moduleForm.certificate.editText')
        : translate('moduleForm.certificate.newText');
    const submitText = edit
        ? translate('moduleForm.certificate.editButton')
        : translate('moduleForm.certificate.newButton');

    return (
        <Formik
            initialValues={initValues}
            onSubmit={(values) => {
                const newValues = { ...values };

                if (certificateChanged && selectedCertificateId) {
                    newValues.certificateId = selectedCertificateId;
                }

                return onSubmit(newValues);
            }}
            {...other}
        >
            {({ submitForm, isSubmitting, dirty }) => (
                <Box sx={{ p: { xs: 2, sm: 4 } }}>
                    <ExitPageAlert
                        alert={UnsavedChangesAlert}
                        when={dirty && !isSubmitting}
                        onConfirm={submitForm}
                    />

                    <Form>
                        {formTitle && formText && (
                            <Box sx={{ mb: 4 }}>
                                {formTitle && (
                                    <Typography
                                        sx={{ fontWeight: 'bold' }}
                                        variant="h3"
                                    >
                                        {formTitle}
                                    </Typography>
                                )}

                                {formText && (
                                    <Box sx={{ mt: 1 }}>
                                        <Typography>{formText}</Typography>
                                    </Box>
                                )}
                            </Box>
                        )}

                        <Box sx={{ mb: 2, mt: 2 }}>
                            <CertificateFormItem
                                certificate={selectedCertificate}
                                onReplaceCertificate={onReplaceCertificate}
                            />

                            {!selectedCertificate && (
                                <FormHelperText error>
                                    {translate(
                                        'moduleForm.certificate.certificateIsRequired'
                                    )}
                                </FormHelperText>
                            )}
                        </Box>

                        <Field
                            component={TextField}
                            label={translate(
                                'moduleForm.certificate.titleOverrideLabel'
                            )}
                            name="titleOverride"
                            placeholder={translate(
                                'moduleForm.certificate.titleOverridePlaceholder'
                            )}
                        />

                        <Box sx={{ mt: 4 }}>
                            <ContentExpander
                                expand={settingsExpanded}
                                title={translate('moreSettings')}
                                onClick={() =>
                                    setSettingsExpanded(!settingsExpanded)
                                }
                            >
                                <FormSection
                                    description={
                                        <Trans
                                            components={{
                                                a: (
                                                    <Link
                                                        rel="noreferrer"
                                                        underline="always"
                                                    />
                                                ),
                                            }}
                                            i18nKey="moduleForm.course.overrideDescription"
                                        />
                                    }
                                    title={translate('titleOverride')}
                                >
                                    <Field
                                        component={TextField}
                                        label={translate('title')}
                                        name="title"
                                    />

                                    <Field
                                        multiline
                                        component={TextField}
                                        label={translate('description')}
                                        name="description"
                                    />
                                </FormSection>

                                <ModuleImageFormSection />
                            </ContentExpander>
                        </Box>

                        <Box sx={{ display: 'flex', mt: 4 }}>
                            <Box>
                                <Button
                                    color="primary"
                                    disabled={
                                        isSubmitting ||
                                        disabled ||
                                        !selectedCertificate ||
                                        (!isSubmitting &&
                                            !disabled &&
                                            !dirty &&
                                            !certificateChanged)
                                    }
                                    loading={isSubmitting}
                                    type="submit"
                                    variant="contained"
                                    onClick={(
                                        e: React.MouseEvent<HTMLButtonElement>
                                    ) => {
                                        e.preventDefault();

                                        return submitForm();
                                    }}
                                >
                                    {submitText}
                                </Button>
                            </Box>
                            {onClose && (
                                <Box sx={{ ml: 2 }}>
                                    <Button
                                        disabled={isSubmitting}
                                        variant="contained"
                                        onClick={onClose}
                                    >
                                        {translate('cancel')}
                                    </Button>
                                </Box>
                            )}
                            {onDelete && (
                                <Box sx={{ ml: 'auto' }}>
                                    <FormDeleteButton
                                        disabled={isSubmitting || disabled}
                                        onClick={onDelete}
                                    >
                                        {translate('delete')}
                                    </FormDeleteButton>
                                </Box>
                            )}
                        </Box>
                    </Form>
                </Box>
            )}
        </Formik>
    );
};
